import { useEffect, useMemo, useState, useSyncExternalStore, type FormEvent } from "react";
import { useSearchParams } from "react-router-dom";
import Modal, { Fold } from "./Modal";
import SearchSelect from "./SearchSelect";
import { Empty, ErrorState, Loading } from "./States";
import { useAuth } from "../lib/auth";
import { api, ApiError, ENTITY_CATEGORIES, type Account, type EntityGroup } from "../lib/api";
import { useResource } from "../lib/useResource";

/**
 * "Which entities?" -- ONE control, the same on every page that asks.
 *
 * Sarah, 2026-09-15: *"select which companies to group together and then see
 * total cash available ... money in and money out for the day, but only for
 * specific entities."* The categories from 025 are one way to cut the book
 * (fixed, one per entity); a saved group is the other (any set, named, kept).
 *
 * Jonathan, the same evening, on the first version: *"There should be one
 * global group option that has the same button by all the pages that sets
 * groups that can be used all over. Right now there is a button over here
 * and over there."* So this is the whole thing: a select that picks the
 * scope, and beside it one **Groups** button that opens the one place groups
 * are made, changed and removed. Both pages render `<GroupsControl>` and
 * nothing else. The chosen scope travels in the URL (`?group=` /
 * `?category=`), so a cut can be bookmarked and sent.
 *
 * A group holds whole entities and single accounts (Sarah, 2026-09-16: *"I
 * dont see the Morgan stanley accounts avail to add to the groups -- also
 * not credit cards"*). A whole entity means every account it has, now and
 * later; an account on its own means just that one. The form shows the
 * accounts under each entity so either can be ticked.
 *
 * A group is never an access boundary. The server computes a group's total
 * over what the reader can see and reports how many members it left out.
 */

export const DASHBOARD_CATEGORIES: Record<string, string[]> = {
  business: ["operating", "investment"], personal_all: ["personal", "personal_entity"],
};

export type Scope = { group?: string; category?: string };

/**
 * One signal for "a group changed", shared by everything that holds a copy
 * of the groups list. The control fetches the list to fill its select; a
 * page fetches it again to filter its rows. They are two fetches of the
 * same thing, and on 2026-09-16 the second one never heard about the first
 * one's edits -- Sarah unticked two cards, saved, and the page kept showing
 * them (*"I cant uncheck the subs"*). Every copy now keys on this counter,
 * and `changed()` below bumps it.
 */
let groupsVersion = 0;
const listeners = new Set<() => void>();
export function bumpGroups() {
  groupsVersion += 1;
  for (const notify of listeners) notify();
}
export function useGroupsVersion(): number {
  return useSyncExternalStore(
    (notify) => {
      listeners.add(notify);
      return () => listeners.delete(notify);
    },
    () => groupsVersion,
  );
}

const UNCLASSIFIED = "unclassified";

export function useScope(): [Scope, (next: Scope) => void] {
  const [params, setParams] = useSearchParams();
  const scope: Scope = useMemo(() => {
    const group = params.get("group");
    const category = params.get("category");
    return group ? { group } : category ? { category } : {};
  }, [params]);
  function set(next: Scope) {
    const updated = new URLSearchParams(params);
    updated.delete("group");
    updated.delete("category");
    if (next.group) updated.set("group", next.group);
    else if (next.category) updated.set("category", next.category);
    setParams(updated, { replace: true });
  }
  return [scope, set];
}

export function scopeKey(scope: Scope): string {
  return scope.group ? `g:${scope.group}` : scope.category ? `c:${scope.category}` : "";
}

/**
 * For a page that filters rows in the browser (accounts, entities): does this
 * row fall inside the chosen scope? `null` scope matches everything. A group
 * matches its members the viewer can see -- which is what the server would
 * answer too, since `/api/groups` names only those.
 *
 * With an `accountId`, the row is an account: in scope when its entity is a
 * whole member or it is a member itself. Without one, the row is an entity:
 * in scope when it is a whole member or any of its accounts is a member --
 * so the entities page still shows Vineyard Family Holdings for a group
 * holding only its two brokerage accounts.
 */
export function useScopeMatch(
  scope: Scope,
): (entityId: string, category: string | null, accountId?: string) => boolean {
  const version = useGroupsVersion();
  const groups = useResource(() => api.groups(), [scope.group ?? "", version]);
  const sets = useMemo(() => {
    if (!scope.group) return null;
    const group = groups.data?.groups.find((g) => g.id === scope.group);
    if (!group) return null;
    return {
      entities: new Set(group.members.map((m) => m.id)),
      accounts: new Set(group.accounts.map((a) => a.id)),
      accountEntities: new Set(group.accounts.map((a) => a.entity_id)),
    };
  }, [groups.data, scope.group]);
  return (entityId, category, accountId) => {
    if (scope.group) {
      if (!sets) return true;
      if (sets.entities.has(entityId)) return true;
      if (accountId !== undefined) return sets.accounts.has(accountId);
      return sets.accountEntities.has(entityId);
    }
    if (scope.category) {
      if (DASHBOARD_CATEGORIES[scope.category]) return category !== null && DASHBOARD_CATEGORIES[scope.category].includes(category);
      return scope.category === UNCLASSIFIED ? category === null : category === scope.category;
    }
    return true;
  };
}

/** Approvers decide on payments; the rest keep the groups. Mirrors the server. */
export function useMayEditGroups(): boolean {
  const { user } = useAuth();
  return Boolean(user && (user.role !== "approver" || user.manages_access));
}

/**
 * The control. Fetches the groups itself so every page shows the same list,
 * and tells the page when the list changed so its figures can follow.
 */
export default function GroupsControl({
  scope,
  onChange,
  onGroupsChanged,
}: {
  scope: Scope;
  onChange: (next: Scope) => void;
  onGroupsChanged?: () => void;
}) {
  const version = useGroupsVersion();
  const groups = useResource(() => api.groups(), [version]);
  const [open, setOpen] = useState(false);
  const rows = groups.data?.groups ?? [];

  function changed() {
    bumpGroups();
    onGroupsChanged?.();
  }

  return (
    <>
      {/* Labelled "Scope", not "Entity" (Jonathan + Sarah, 2026-10-25). This
          control picks a CATEGORY or a saved GROUP — a coarse scope over the
          whole page — while the single-entity picker in the Filters panel is the
          one that is actually "Entity". Both were called "Entity", which is the
          confusion they flagged; the names are now distinct. */}
      <SearchSelect
        label="Scope"
        ariaLabel="Scope — a category or a saved group"
        value={scopeKey(scope)}
        placeholder="All entities"
        options={[
          { value: "", label: "All entities" },
          { value: "c:business", label: "Business", group: "Dashboard views" },
          { value: "c:personal_all", label: "Personal", group: "Dashboard views" },
          ...ENTITY_CATEGORIES.map((c) => ({
            value: `c:${c.key}`,
            label: c.label,
            group: "By category",
          })),
          { value: `c:${UNCLASSIFIED}`, label: "Not classified", group: "By category" },
          ...rows.map((g) => ({
            value: `g:${g.id}`,
            label: `${g.name} (${g.members_total})`,
            group: "Groups",
          })),
        ]}
        onChange={(value) => {
          if (!value) onChange({});
          else if (value.startsWith("g:")) onChange({ group: value.slice(2) });
          else onChange({ category: value.slice(2) });
        }}
      />
      <button type="button" onClick={() => setOpen(true)}>
        Groups
        {rows.length > 0 ? <span className="pill count">{rows.length}</span> : null}
      </button>
      {open ? (
        <GroupsModal
          groups={rows}
          status={groups.status}
          onUse={(id) => {
            onChange({ group: id });
            setOpen(false);
          }}
          onChanged={changed}
          onClose={() => setOpen(false)}
        />
      ) : null}
    </>
  );
}

/** "3 entities · 4 accounts", with the zero half left out. */
function describeMembers(g: { entities_total: number; accounts_total: number }): string {
  const parts: string[] = [];
  if (g.entities_total > 0) parts.push(`${g.entities_total} entit${g.entities_total === 1 ? "y" : "ies"}`);
  if (g.accounts_total > 0) parts.push(`${g.accounts_total} account${g.accounts_total === 1 ? "" : "s"}`);
  return parts.join(" · ") || "nothing you can see";
}

/**
 * The one place groups are kept. A list, and -- behind New / Change -- the
 * form, inside the same modal so nothing on the page moves.
 */
export function GroupsModal({
  groups,
  status,
  onUse,
  onChanged,
  onClose,
}: {
  groups: EntityGroup[];
  status: string;
  onUse: (id: string) => void;
  onChanged: () => void;
  onClose: () => void;
}) {
  const mayEdit = useMayEditGroups();
  const [editing, setEditing] = useState<"new" | EntityGroup | null>(null);
  const [removing, setRemoving] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function remove(group: EntityGroup) {
    setError(null);
    try {
      await api.retireGroup(group.id);
      setRemoving(null);
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : "Could not remove it.");
    }
  }

  if (editing !== null) {
    return (
      <GroupForm
        existing={editing === "new" ? null : editing}
        onDone={() => {
          setEditing(null);
          onChanged();
        }}
        onCancel={() => setEditing(null)}
      />
    );
  }

  return (
    <Modal
      title="Groups"
      onClose={onClose}
      actions={
        <>
          {mayEdit ? (
            <button type="button" className="primary" onClick={() => setEditing("new")}>
              New group
            </button>
          ) : null}
          <button type="button" onClick={onClose}>
            Close
          </button>
        </>
      }
    >
      {/* The one caveat worth keeping: a group is a view, not a grant. */}
      <div className="band-note" style={{ margin: "0 0 1em" }}>
        Saved views
        
      </div>
      {error ? <div className="form-error">{error}</div> : null}
      {status === "loading" ? (
        <Loading rows={3} fill={false} />
      ) : groups.length === 0 ? (
        <Empty headline="No groups yet." />
      ) : (
        <div className="group-list">
          {groups.map((g) => (
            <div key={g.id} className="group-row">
              <div className="group-name">
                <strong>{g.name}</strong>
                <span className="dim">
                  {" "}
                  · {describeMembers(g)}
                  {g.members_hidden > 0 ? ` · ${g.members_hidden} outside your reach` : ""}
                </span>
                <div className="group-members dim">
                  {[
                    ...g.members.map((m) => m.name),
                    ...g.accounts.map((a) => `${a.name}${a.last4 ? ` ··${a.last4}` : ""}`),
                  ].join(", ")}
                </div>
              </div>
              <div className="inline-actions">
                {removing === g.id ? (
                  <>
                    <span className="band-note">Remove it?</span>
                    <button type="button" className="link" onClick={() => void remove(g)}>
                      Yes, remove
                    </button>
                    <button type="button" onClick={() => setRemoving(null)}>
                      Keep
                    </button>
                  </>
                ) : (
                  <>
                    <button type="button" className="primary" onClick={() => onUse(g.id)}>
                      Use
                    </button>
                    {mayEdit ? (
                      <>
                        <button type="button" onClick={() => setEditing(g)}>
                          Change
                        </button>
                        <button type="button" className="link" onClick={() => setRemoving(g.id)}>
                          Remove
                        </button>
                      </>
                    ) : null}
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
}

/**
 * Make or change a group: a name, and the entities you can see in folded
 * category sections -- each head says how many of its entities are picked,
 * and opens to the tick boxes. Under every entity sit its accounts, so a
 * group can be "all of Vineyard Family Holdings" or just "Vine One and Vine
 * Two". Members outside your reach are not on the form and are kept as they
 * are by the server.
 *
 * The credit cards are listed once more on their own, in a fold above the
 * categories, so a card is found without opening its company (`cards`).
 *
 * Two sets, kept disjoint: `whole` is the entities picked outright; `single`
 * the accounts picked on their own, never one whose entity is in `whole`.
 * Ticking the last account of an entity promotes it to `whole` (so the group
 * follows the entity's future accounts); unticking one demotes it back.
 */
function GroupForm({
  existing,
  onDone,
  onCancel,
}: {
  existing: EntityGroup | null;
  onDone: () => void;
  onCancel: () => void;
}) {
  const entities = useResource(() => api.entities(), []);
  const accounts = useResource(() => api.accounts(), []);
  const [name, setName] = useState(existing?.name ?? "");
  const [whole, setWhole] = useState<Set<string>>(
    () => new Set(existing?.members.map((m) => m.id) ?? []),
  );
  const [single, setSingle] = useState<Set<string>>(
    () => new Set(existing?.accounts.map((a) => a.id) ?? []),
  );
  const [openKey, setOpenKey] = useState<string | null>(null);
  const [openEntity, setOpenEntity] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setName(existing?.name ?? "");
    setWhole(new Set(existing?.members.map((m) => m.id) ?? []));
    setSingle(new Set(existing?.accounts.map((a) => a.id) ?? []));
    setError(null);
  }, [existing]);

  const accountsOf = useMemo(() => {
    const map = new Map<string, Account[]>();
    for (const a of accounts.data?.accounts ?? []) {
      if (a.retired_on) continue;
      const list = map.get(a.entity_id) ?? [];
      list.push(a);
      map.set(a.entity_id, list);
    }
    for (const list of map.values()) list.sort((x, y) => x.name.localeCompare(y.name));
    return map;
  }, [accounts.data]);

  /**
   * The credit cards, listed on their own as well as under their entity
   * (Sarah, 2026-09-16, on the Amex cards: *"keep all amex cards separate,
   * not under the main account name"* -- *"on its own rather than under
   * company"*). A card is still its entity's account, so ticking it here and
   * ticking it under the entity are the same tick; this is only a second
   * place to find it, at the top, sorted by card rather than by company.
   */
  const cards = useMemo(
    () =>
      [...accountsOf.values()]
        .flat()
        .filter((a) => a.account_class === "debt")
        .sort((x, y) => x.name.localeCompare(y.name) || x.entity_name.localeCompare(y.entity_name)),
    [accountsOf],
  );

  const sections = useMemo(() => {
    const rows = entities.data?.entities ?? [];
    const specs = [
      ...ENTITY_CATEGORIES.map((c) => ({ key: c.key as string, label: c.label })),
      { key: UNCLASSIFIED, label: "Not classified" },
    ];
    return specs
      .map((spec) => ({
        ...spec,
        rows: rows
          .filter((e) => !e.retired_on && (e.category ?? UNCLASSIFIED) === spec.key)
          .sort((a, b) => a.name.localeCompare(b.name)),
      }))
      .filter((spec) => spec.rows.length > 0);
  }, [entities.data]);

  /** How many of an entity's accounts are picked, counting a whole entity as all. */
  function pickedIn(entityId: string): number {
    const list = accountsOf.get(entityId) ?? [];
    if (whole.has(entityId)) return list.length;
    return list.filter((a) => single.has(a.id)).length;
  }

  function setEntity(entityId: string, on: boolean) {
    const ids = (accountsOf.get(entityId) ?? []).map((a) => a.id);
    setWhole((prev) => {
      const next = new Set(prev);
      if (on) next.add(entityId);
      else next.delete(entityId);
      return next;
    });
    setSingle((prev) => {
      const next = new Set(prev);
      for (const id of ids) next.delete(id);
      return next;
    });
  }

  function toggleEntity(entityId: string) {
    // Partly picked counts as "not whole": the click makes it whole.
    setEntity(entityId, !whole.has(entityId));
  }

  function toggleAccount(entityId: string, accountId: string) {
    const ids = (accountsOf.get(entityId) ?? []).map((a) => a.id);
    if (whole.has(entityId)) {
      // Whole → all but this one, as single accounts.
      setWhole((prev) => {
        const next = new Set(prev);
        next.delete(entityId);
        return next;
      });
      setSingle((prev) => {
        const next = new Set(prev);
        for (const id of ids) if (id !== accountId) next.add(id);
        return next;
      });
      return;
    }
    const next = new Set(single);
    if (next.has(accountId)) next.delete(accountId);
    else next.add(accountId);
    if (ids.length > 0 && ids.every((id) => next.has(id))) {
      setEntity(entityId, true);
    } else {
      setSingle(next);
    }
  }

  /** A card is picked if it was ticked itself or its whole entity was. */
  function cardOn(a: Account): boolean {
    return whole.has(a.entity_id) || single.has(a.id);
  }

  function toggleAllCards() {
    const every = cards.every(cardOn);
    for (const a of cards) if (cardOn(a) === every) toggleAccount(a.entity_id, a.id);
  }

  function toggleAll(entityIds: string[]) {
    const every = entityIds.every((id) => whole.has(id));
    for (const id of entityIds) setEntity(id, !every);
  }

  async function submit(event?: FormEvent) {
    event?.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const body = { name: name.trim(), entity_ids: [...whole], account_ids: [...single] };
      if (existing) await api.updateGroup(existing.id, body);
      else await api.createGroup(body);
      onDone();
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : "Something went wrong; nothing was saved.");
    } finally {
      setBusy(false);
    }
  }

  const picked = whole.size + single.size;
  const canSave = !busy && picked > 0 && name.trim().length > 0;
  const loading = entities.status === "loading" || accounts.status === "loading";
  const failed =
    entities.status === "error" && entities.error
      ? { error: entities.error, reload: entities.reload }
      : accounts.status === "error" && accounts.error
        ? { error: accounts.error, reload: accounts.reload }
        : null;

  return (
    <Modal
      title={existing ? `Change ${existing.name}` : "New group"}
      onClose={onCancel}
      actions={
        <>
          <button type="button" className="primary" disabled={!canSave} onClick={() => void submit()}>
            {busy ? "Saving…" : existing ? "Save" : "Create"}
          </button>
          <button type="button" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
        </>
      }
    >
      <form onSubmit={submit} className="group-form">
        {error ? <div className="form-error">{error}</div> : null}
        <div className="field">
          <label htmlFor="group-name">Name</label>
          <input
            id="group-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Motivated Care"
            maxLength={80}
            autoFocus
            required
          />
        </div>
        {existing && existing.members_hidden > 0 ? (
          <div className="band-note">
            +{existing.members_hidden} outside your reach, kept
          </div>
        ) : null}
        <div className="field">
          <label>
            Entities and accounts{" "}
            <span className="pill neutral count">
              {describeMembers({ entities_total: whole.size, accounts_total: single.size })
                .replace("nothing you can see", "nothing picked")}
            </span>
            
          </label>
        </div>
        {loading ? (
          <Loading rows={4} fill={false} />
        ) : failed ? (
          <ErrorState error={failed.error} onRetry={failed.reload} />
        ) : sections.length === 0 ? (
          <Empty headline="No entities." />
        ) : (
          <>
            {cards.length > 0 ? (
              <Fold
                label="Credit cards"
                summary={`${cards.filter(cardOn).length} of ${cards.length}`}
                open={openKey === "cards"}
                onToggle={() => setOpenKey(openKey === "cards" ? null : "cards")}
                trailing={
                  <button type="button" className="link" onClick={toggleAllCards}>
                    {cards.every(cardOn) ? "None" : "All"}
                  </button>
                }
              >
                <div className="pick-list pick-cards">
                  {cards.map((a) => (
                    <label key={a.id} className="pick-row">
                      <input
                        type="checkbox"
                        checked={cardOn(a)}
                        onChange={() => toggleAccount(a.entity_id, a.id)}
                      />
                      <span>
                        {a.name}
                        {a.last4 ? <span className="dim"> ··{a.last4}</span> : null}
                        <span className="dim"> · {a.entity_name}</span>
                      </span>
                    </label>
                  ))}
                </div>
              </Fold>
            ) : null}
            {sections.map((section) => {
              const ids = section.rows.map((e) => e.id);
              const touched = ids.filter((id) => whole.has(id) || pickedIn(id) > 0).length;
              const all = ids.every((id) => whole.has(id));
              return (
                <Fold
                  key={section.key}
                  label={section.label}
                  summary={`${touched} of ${ids.length}`}
                  open={openKey === section.key}
                  onToggle={() => setOpenKey(openKey === section.key ? null : section.key)}
                  trailing={
                    <button type="button" className="link" onClick={() => toggleAll(ids)}>
                      {all ? "None" : "All"}
                    </button>
                  }
                >
                  <div className="pick-list">
                    {section.rows.map((e) => {
                      const list = accountsOf.get(e.id) ?? [];
                      const n = pickedIn(e.id);
                      const isWhole = whole.has(e.id);
                      const partial = !isWhole && n > 0;
                      const open = openEntity === e.id;
                      return (
                        <div key={e.id}>
                          <label className="pick-row">
                            <input
                              type="checkbox"
                              checked={isWhole}
                              ref={(el) => {
                                if (el) el.indeterminate = partial;
                              }}
                              onChange={() => toggleEntity(e.id)}
                            />
                            <span>{e.name}</span>
                            {list.length > 0 ? (
                              <button
                                type="button"
                                className="link pick-accounts"
                                aria-expanded={open}
                                onClick={(ev) => {
                                  ev.preventDefault();
                                  setOpenEntity(open ? null : e.id);
                                }}
                              >
                                {n} of {list.length} account{list.length === 1 ? "" : "s"} {open ? "▾" : "▸"}
                              </button>
                            ) : (
                              <span className="pick-accounts">No accounts</span>
                            )}
                          </label>
                          {open ? (
                            <div className="pick-sub pick-list">
                              {list.map((a) => (
                                <label key={a.id} className="pick-row">
                                  <input
                                    type="checkbox"
                                    checked={isWhole || single.has(a.id)}
                                    onChange={() => toggleAccount(e.id, a.id)}
                                  />
                                  <span>
                                    {a.name}
                                    {a.last4 ? <span className="dim"> ··{a.last4}</span> : null}
                                    <span className="dim"> · {a.institution_name}</span>
                                  </span>
                                </label>
                              ))}
                            </div>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                </Fold>
              );
            })}
          </>
        )}
      </form>
    </Modal>
  );
}
